/**
 * NatureObservations — /nature/observations
 * Community feed of nature sightings. Every AI photo/audio ID performed
 * by any signed-in user also lands here as an observation.
 *
 * Feed backend: GET /api/nature/observations (from routes/nature.py)
 *   scope=all|mine|near, kind=bird|plant|…, species="…"
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Camera, Search, ArrowLeft, Loader2, Users, User as UserIcon,
  Volume2, ImageIcon, Sparkles, ArrowRight,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const KIND_TABS = [
  { key: "",         label: "All" },
  { key: "bird",     label: "Birds" },
  { key: "plant",    label: "Plants" },
  { key: "tree",     label: "Trees" },
  { key: "flower",   label: "Flowers" },
  { key: "mammal",   label: "Mammals" },
  { key: "insect",   label: "Insects" },
  { key: "butterfly",label: "Butterflies" },
  { key: "fish",     label: "Fish" },
  { key: "mushroom", label: "Mushrooms" },
];

export default function NatureObservations() {
  const { user } = useAuth();
  const [scope, setScope]     = useState("all");
  const [kind,  setKind]      = useState("");
  const [q,     setQ]         = useState("");
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [busy,  setBusy]      = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("scope", scope);
      params.set("limit", "48");
      if (kind) params.set("kind", kind);
      if (q)    params.set("species", q);
      const { data } = await http.get(`/nature/observations?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setItems([]); setTotal(0);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, kind]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="nature-observations">
      <Link to="/nature" className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Nature hub
      </Link>
      <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>CITIZEN SCIENCE</div>
      <h1 className="font-display text-3xl md:text-4xl mb-1 inline-flex items-center gap-2">
        <Sparkles className="w-7 h-7" /> Community observations
      </h1>
      <p className="opacity-80 mb-6 max-w-3xl">
        Every AI identification our community makes lands here — a growing,
        living map of life on Earth. Snap a photo, record a bird song, and
        add your sighting.
      </p>

      {/* Scope + CTA row */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex rounded-full border p-0.5"
             style={{ borderColor: "var(--wofy-border)" }}
             data-testid="nature-obs-scope">
          <button type="button" onClick={() => setScope("all")}
                  data-testid="nature-obs-scope-all"
                  className={`text-xs px-3 py-1 rounded-full inline-flex items-center gap-1 ${scope === "all" ? "bg-black text-white" : "hover:bg-black/5"}`}>
            <Users className="w-3 h-3" /> Everyone
          </button>
          {user && (
            <button type="button" onClick={() => setScope("mine")}
                    data-testid="nature-obs-scope-mine"
                    className={`text-xs px-3 py-1 rounded-full inline-flex items-center gap-1 ${scope === "mine" ? "bg-black text-white" : "hover:bg-black/5"}`}>
              <UserIcon className="w-3 h-3" /> My observations
            </button>
          )}
        </div>
        <div className="ml-auto flex gap-2">
          <Link to="/nature/identify" className="wofy-btn-primary text-sm" data-testid="nature-obs-add">
            <Camera className="w-4 h-4" /> Add via AI ID
          </Link>
        </div>
      </div>

      {/* Kind filter */}
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="nature-obs-kinds">
        {KIND_TABS.map(t => (
          <button
            key={t.key || "all"} type="button" onClick={() => setKind(t.key)}
            data-testid={`nature-obs-kind-${t.key || "all"}`}
            className={`text-xs px-3 py-1 rounded-full border ${kind === t.key ? "bg-black text-white" : "hover:bg-black/5"}`}
            style={kind === t.key ? undefined : { borderColor: "var(--wofy-border)" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Species search */}
      <form onSubmit={onSearch} className="mb-6 flex gap-2 max-w-lg">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by species name…"
            className="w-full pl-9 pr-3 py-2 rounded-full border text-sm outline-none focus:ring-2"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="nature-obs-search-input"
          />
        </div>
        <button type="submit" className="wofy-btn-ghost text-sm" data-testid="nature-obs-search-submit">
          Search
        </button>
      </form>

      {/* Feed */}
      {busy && (
        <div className="wofy-card !p-6 inline-flex items-center gap-2" data-testid="nature-obs-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading observations…
        </div>
      )}
      {!busy && items.length === 0 && (
        <div className="wofy-card !p-6" data-testid="nature-obs-empty">
          <div className="font-medium mb-1">No observations yet.</div>
          <div className="opacity-70 text-sm">
            {scope === "mine"
              ? <>You haven't logged an observation. <Link to="/nature/identify" className="underline">Identify a photo</Link> and it will show up here.</>
              : <>Be the first to add a sighting.</>}
          </div>
        </div>
      )}
      {!busy && items.length > 0 && (
        <>
          <div className="text-xs opacity-60 mb-2" data-testid="nature-obs-total">
            Showing {items.length} of {total} observation{total === 1 ? "" : "s"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="nature-obs-list">
            {items.map((o) => <ObservationCard key={o.id} o={o} />)}
          </div>
        </>
      )}
    </div>
  );
}

function ObservationCard({ o }) {
  const dt = o.observed_at || o.created_at;
  const when = dt ? new Date(dt).toLocaleDateString() : null;
  const isAudio = o.source === "ai_audio";
  const isAI    = o.source === "ai_identify" || o.source === "ai_audio";

  return (
    <div className="rounded-xl border overflow-hidden hover:shadow-sm transition"
         style={{ borderColor: "var(--wofy-border)" }}
         data-testid={`nature-obs-card-${o.id}`}>
      {o.image_url ? (
        <div className="aspect-video bg-black/5 relative">
          <img src={o.image_url} alt={o.common_name || "observation"} className="w-full h-full object-cover" />
          {isAI && (
            <span className="absolute top-2 left-2 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full"
                  style={{ background: "rgba(0,0,0,0.7)", color: "white" }}>
              {isAudio ? "AI · audio" : "AI · photo"}
            </span>
          )}
        </div>
      ) : (
        <div className="aspect-video bg-black/5 flex items-center justify-center opacity-60">
          {isAudio
            ? <Volume2 className="w-8 h-8" />
            : <ImageIcon className="w-8 h-8" />}
        </div>
      )}
      <div className="p-3">
        <div className="text-xs opacity-60 font-mono uppercase tracking-wider mb-0.5">
          {o.category || o.kind || "other"}
        </div>
        <div className="font-medium leading-tight">{o.common_name || "Unknown species"}</div>
        {o.scientific_name && (
          <div className="text-[11px] font-mono italic opacity-70 mt-0.5">{o.scientific_name}</div>
        )}
        <div className="mt-2 flex items-center justify-between gap-2 text-xs opacity-80">
          <div className="truncate">{o.user_name || "Anonymous"}</div>
          {when && <div className="whitespace-nowrap opacity-70">{when}</div>}
        </div>
        {o.location_name && (
          <div className="mt-1 text-xs opacity-70 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {o.location_name}
          </div>
        )}
        {o.canonical_slug && (
          <Link to={`/nature/species/${o.canonical_slug}`}
                className="mt-2 inline-flex items-center gap-1 text-xs hover:underline"
                style={{ color: "var(--wofy-primary)" }}
                data-testid={`nature-obs-view-${o.id}`}>
            View species profile <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>
    </div>
  );
}
